/**
 * tools/recentlyClickedStore.ts — the per-browser "recently clicked tools" list.
 *
 * WHAT THIS IS (and is NOT). This is BROWSING history: the tools whose detail page the viewer has
 * opened, most-recent-first. It is deliberately NOT search history — AE-F36 / DS-F19 forbid storing
 * a "recent searches" list, and this stores nothing about what was typed into the search box. A tool
 * lands here only when its /tools/:toolId page is viewed (see ToolDetail.tsx's record effect), which
 * is a navigation the user chose, not a query. The search dropdown's empty state reads this to offer
 * "Recently clicked" above "Popular".
 *
 * The store shape is copied VERBATIM from upload/skinStore.ts — the defensive `storage()` probe, the
 * in-memory mirror, the `useSyncExternalStore` hook, and the cross-tab `storage` listener. That file
 * makes the case for copying rather than sharing: a shared helper would couple two unrelated modules,
 * and each must degrade to "works in-memory, best-effort persistence" rather than throw in a
 * privacy-mode browser where merely touching localStorage raises.
 */
import { useSyncExternalStore } from 'react';

/** One remembered tool. `ts` is the click time (ms epoch), kept only to order the list. */
export interface RecentTool {
  toolId: string;
  name: string;
  ts: number;
}

/** Bump if the stored shape changes. */
const KEY = 'tb.recentTools.v1';
/** How many to keep — a short list; the dropdown shows the top few. */
const CAP = 8;

/**
 * localStorage, or null when it cannot be used. Copied from skinStore.ts on purpose (see header):
 * merely probing storage throws in some privacy modes, so it is treated as "unavailable" (→ empty
 * list, best-effort writes) rather than allowed to crash the page.
 */
function storage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const probe = `${KEY}:__probe__`;
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Parse the stored JSON into a clean, capped list, discarding anything malformed. */
function parse(raw: string | null): RecentTool[] {
  if (!raw) return [];
  try {
    const data: unknown = JSON.parse(raw);
    if (!Array.isArray(data)) return [];
    const out: RecentTool[] = [];
    for (const item of data) {
      if (
        item &&
        typeof item === 'object' &&
        typeof (item as RecentTool).toolId === 'string' &&
        typeof (item as RecentTool).name === 'string' &&
        typeof (item as RecentTool).ts === 'number'
      ) {
        out.push({
          toolId: (item as RecentTool).toolId,
          name: (item as RecentTool).name,
          ts: (item as RecentTool).ts,
        });
      }
    }
    return out.slice(0, CAP);
  } catch {
    return [];
  }
}

/** In-memory mirror so a privacy-mode browser (no storage) still remembers for the current session. */
let current: RecentTool[] = readInitial();
const listeners = new Set<() => void>();

function readInitial(): RecentTool[] {
  const store = storage();
  if (!store) return [];
  try {
    return parse(store.getItem(KEY));
  } catch {
    return [];
  }
}

export function getRecentTools(): RecentTool[] {
  return current;
}

/**
 * Record that a tool's detail page was opened: put it at the FRONT, drop any earlier entry for the
 * same tool (so a re-visit moves it up rather than duplicating), and cap the list. A no-op when the
 * tool is already at the front with the same name — avoids a needless write/notify on every render
 * of the same page.
 */
export function recordToolClick(toolId: string, name: string): void {
  const head = current[0];
  if (head && head.toolId === toolId && head.name === name) return;
  const next: RecentTool[] = [
    { toolId, name, ts: Date.now() },
    ...current.filter((t) => t.toolId !== toolId),
  ].slice(0, CAP);
  current = next;
  const store = storage();
  if (store) {
    try {
      store.setItem(KEY, JSON.stringify(next));
    } catch {
      // Best-effort: the in-memory value still drives this session.
    }
  }
  for (const cb of listeners) cb();
}

export function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  // Cross-tab sync: another tab writing the key fires a `storage` event here.
  function onStorage(e: StorageEvent) {
    if (e.key !== KEY) return;
    current = parse(e.newValue);
    for (const l of listeners) l();
  }
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(cb);
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  };
}

/** React hook: the current recently-clicked list, re-rendering on change (including cross-tab). */
export function useRecentTools(): RecentTool[] {
  return useSyncExternalStore(subscribe, getRecentTools, () => []);
}
